import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PageHeader from '../../../components/owner/PageHeader';
import {
  ColumnFilterHeader,
  formatDateFilterValue,
  matchAmountFilter,
  matchTextFilter,
  useColumnFilters,
} from '../../../components/admin/ColumnFilterHeader';
import {
  fetchMembershipsPage,
  deleteMembership,
  updateMembershipSale,
} from '../../../services/receptionist/receptionistApi';
import PrintActionsHeader from '../../../components/shared/PrintActionsHeader';
import TableRowIconActions from '../../../components/shared/TableRowIconActions';
import { usePanelToast } from '../../../context/PanelToastContext';
import { fetchAllPackages } from '../../../services/gym/catalogApi';
import { membershipCanPayBalance, membershipPendingAmount, membershipTotalAmount } from '../../../utils/membershipFees';
import {
  membershipStartDisplay,
  membershipExpiryDisplay,
  membershipDurationDisplay,
  membershipDateDisplay,
  membershipPaymentNoteDisplay,
  sortMembershipsByDateAsc,
} from '../../../utils/membershipDates';
import { dedupeMemberships } from '../../../utils/dedupeList';
import {
  exportMembershipCsv,
  exportMembershipExcel,
  exportMembershipPdf,
  exportMemberships,
  printMembershipList,
} from './exportMemberships';
import { formatCurrency } from '../packages/packageData';
import { formatSerialDisplay } from '../members/memberData';
import TablePagination from '../../../components/shared/TablePagination';
import { PAYMENTS_UPDATE_EVENT } from '../../receptionist/paymentStore';
import {
  SaleMethodColumnHeaders,
  SaleMethodCells,
  SALE_METHOD_COLUMN_COUNT,
  saleMethodFilterDefaults,
  matchSaleMethodFilters,
} from '../../receptionist/sales/SaleMethodTableColumns';
import PayBalanceModal from '../../receptionist/sales/PayBalanceModal';
import SaleReceiptModal from '../../receptionist/sales/SaleReceiptModal';
import SplitPaymentFields from '../../receptionist/sales/SplitPaymentFields';
import { buildReceiptFromPaymentRow } from '../../receptionist/sales/buildSaleReceipt';
import { membershipToPaymentRow, membershipRowForReceipt } from '../../receptionist/sales/salePaymentUtils';
import {
  defaultPaymentSplits,
  sumPaymentSplits,
} from '../../../utils/splitPaymentUtils';
import {
  SUBSCRIPTION_DURATION_OPTIONS,
  subscriptionDurationLabel,
  normalizeSubscriptionDurationLabel,
  addMonthsToEndDate,
} from '../../receptionist/sales/saleDurationUtils';
import '../../owner/ownerPages.css';
import '../../owner/subscriptions/subscriptions.css';
import '../../receptionist/members/members.css';
import '../adminListPages.css';
import '../../owner/finance/finance.css';
import './memberships.css';

const actionTypes = {
  edit: 'edit',
  upgrade: 'upgrade',
  freeze: 'freeze',
  lifetime: 'lifetime',
};

function durationMonthsFromStored(raw) {
  const label = normalizeSubscriptionDurationLabel(raw);
  const found = SUBSCRIPTION_DURATION_OPTIONS.find(
    (o) => o.label.toLowerCase() === String(label || '').toLowerCase(),
  );
  return found ? found.value : '';
}

function getEmptyEditForm() {
  return {
    member: '',
    gymId: '',
    package: '',
    packageFee: '',
    duration: '',
    entryDate: '',
    start: '',
    expiry: '',
    datesTbc: false,
    paymentDbId: null,
    paymentSplits: defaultPaymentSplits(),
    registrationFee: 0,
    discountAmount: 0,
    personalTrainingFee: 0,
    classesFee: 0,
  };
}

function splitsFromMembership(m) {
  if (Array.isArray(m?.paymentSplits) && m.paymentSplits.length) {
    return defaultPaymentSplits().map((row) => {
      const found = m.paymentSplits.find((s) => s.method === row.method);
      return {
        method: row.method,
        amount: found?.amount != null && found.amount !== '' ? String(found.amount) : '',
      };
    });
  }
  const paid = Number(m?.totalPaid) || 0;
  if (paid <= 0) return defaultPaymentSplits();
  return defaultPaymentSplits().map((row, index) => (
    index === 0 ? { ...row, amount: String(paid) } : row
  ));
}

function mapMembershipRow(m) {
  const complimentary = Boolean(m.complimentary)
    || String(m.paymentStatus || '').toLowerCase() === 'complimentary'
    || String(m.status || '').toLowerCase() === 'complimentary';
  return {
    id: m.id,
    memberId: m.memberId,
    memberName: m.member || m.memberName || '—',
    gymId: m.gymId || m.gym_member_id || '—',
    branch: m.branch || '',
    package: m.package || '—',
    duration: m.duration || '',
    serialNo: m.serialNo != null && m.serialNo !== ''
      ? (Number.isFinite(Number(m.serialNo)) ? Number(m.serialNo) : String(m.serialNo).trim())
      : null,
    createdAt: m.createdAt
      || m.joiningDate
      || (m.created_at ? String(m.created_at).slice(0, 10) : '')
      || m.paymentDate
      || '',
    joiningDate: m.joiningDate || '',
    startDate: m.startDate || m.start || '—',
    expiryDate: m.expiry || m.expiryDate || '—',
    datesTbc: Boolean(m.datesTbc),
    complimentary,
    packageFee: complimentary ? 0 : (Number(m.packageFee) || 0),
    registrationFee: complimentary ? 0 : (Number(m.registrationFee) || 0),
    totalAmount: complimentary ? 0 : (Number(m.totalAmount) || 0),
    totalPaid: complimentary ? 0 : (Number(m.totalPaid) || 0),
    pendingAmount: complimentary ? 0 : (Number(m.pendingAmount) || 0),
    paymentSplits: complimentary ? [] : (Array.isArray(m.paymentSplits) ? m.paymentSplits : []),
    paymentDbId: complimentary ? null : (m.paymentDbId || null),
    paymentId: complimentary ? null : (m.paymentId || null),
    invoiceId: complimentary ? null : (m.invoiceId || null),
    paymentStatus: complimentary ? 'complimentary' : (m.paymentStatus || ''),
    paymentDate: m.paymentDate || '',
    paymentNote: m.paymentNote || '',
    paymentSource: m.paymentSource || 'subscription',
    discountAmount: complimentary ? 0 : (Number(m.discountAmount) || 0),
    personalTrainingFee: complimentary ? 0 : (Number(m.personalTrainingFee) || 0),
    classesFee: complimentary ? 0 : (Number(m.classesFee) || 0),
    status: m.frozen ? 'inactive' : m.status,
    frozen: Boolean(m.frozen),
    lifetime: Boolean(m.lifetime),
    autoRenewal: Boolean(m.autoRenewal),
  };
}

function getMembershipStatusLabel(membership) {
  if (membership.datesTbc) return 'TBC';
  if (membership.lifetime) return 'Lifetime';
  if (membership.frozen || membership.status === 'inactive') return 'Inactive';
  if (membership.status === 'expired') return 'Expired';
  return 'Active';
}

function StatusBadge({ membership }) {
  if (membership.datesTbc) {
    return <span className="owner-badge owner-badge-info">TBC</span>;
  }
  if (membership.lifetime) {
    return <span className="owner-badge owner-badge-success">Lifetime</span>;
  }
  if (membership.frozen || membership.status === 'inactive') {
    return <span className="owner-badge owner-badge-warning">Inactive</span>;
  }
  if (membership.status === 'expired') {
    return <span className="owner-badge owner-badge-warning">Expired</span>;
  }
  return <span className="owner-badge owner-badge-success">Active</span>;
}

function MembershipManagement() {
  const toast = usePanelToast();
  const [membershipList, setMembershipList] = useState([]);
  const [packageOptions, setPackageOptions] = useState([]);
  const [packageRows, setPackageRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null);
  const [selectedMembershipId, setSelectedMembershipId] = useState('');
  const [formKey, setFormKey] = useState(0);
  const [editForm, setEditForm] = useState(getEmptyEditForm);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const pageSize = 50;
  const requestIdRef = useRef(0);
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    date: '',
    member: '',
    branch: '',
    package: '',
    serialNo: '',
    duration: '',
    packageFees: '',
    registrationFees: '',
    totalPaid: '',
    pendingAmount: '',
    ...saleMethodFilterDefaults(),
    startDate: '',
    expiryDate: '',
    autoRenewal: '',
    paymentNote: '',
    status: '',
  });
  const [error, setError] = useState('');
  const [payTarget, setPayTarget] = useState(null);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, debouncedFilters, rangeStart, rangeEnd]);

  const loadData = useCallback(async () => {
    const reqId = ++requestIdRef.current;
    try {
      setError('');
      setLoading(true);
      const [result, packages] = await Promise.all([
        fetchMembershipsPage({
          page,
          pageSize,
          search: debouncedSearch,
          filters: { ...debouncedFilters, rangeStart, rangeEnd },
        }),
        fetchAllPackages(),
      ]);
      if (reqId !== requestIdRef.current) return;
      const priceByPackage = new Map(
        (packages || []).map((p) => [
          String(p.name || '').trim().toLowerCase(),
          Number(p.price) || 0,
        ]),
      );
      const durationByPackage = new Map(
        (packages || []).map((p) => [
          String(p.name || '').trim().toLowerCase(),
          String(p.duration || '').trim(),
        ]).filter(([, duration]) => duration),
      );
      let mappedRows = dedupeMemberships((result.rows || []).map((row) => {
        const mapped = mapMembershipRow(row);
        // Keep saved 0 — only catalog-fill when fee missing and no payment linked
        if (
          !mapped.complimentary
          && !mapped.paymentDbId
          && mapped.packageFee !== 0
          && !(Number(mapped.packageFee) > 0)
        ) {
          const catalogPrice = priceByPackage.get(String(mapped.package || '').trim().toLowerCase()) || 0;
          if (catalogPrice > 0) mapped.packageFee = catalogPrice;
        }
        mapped.packageFee = Math.max(0, Number(mapped.packageFee) || 0);
        mapped.duration = membershipDurationDisplay(mapped, durationByPackage);
        if (!mapped.totalAmount) {
          mapped.totalAmount = membershipTotalAmount(mapped);
        }
        if (mapped.paymentDbId) {
          mapped.pendingAmount = membershipPendingAmount(mapped);
        } else if (Number(mapped.pendingAmount) > 0) {
          mapped.pendingAmount = 0;
        }
        return mapped;
      }));
      // Custom date range: keep Date column ascending (3 → 4 → 5)
      if (rangeStart || rangeEnd) {
        mappedRows = sortMembershipsByDateAsc(mappedRows);
      }
      setMembershipList(mappedRows);
      setTotal(result.total || 0);
      setPackageRows(packages || []);
      setPackageOptions(packages.map((p) => p.name).filter(Boolean));
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      setMembershipList([]);
      setTotal(0);
      setError(err.message || 'Failed to load memberships');
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, debouncedFilters, rangeStart, rangeEnd]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const refresh = () => loadData();
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    return () => window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
  }, [loadData]);

  const [upgradeForm, setUpgradeForm] = useState({ newPackage: 'Premium', paymentMethod: 'cash' });
  const [freezeForm, setFreezeForm] = useState({ reason: '', freezeDays: '30' });
  const [lifetimeForm, setLifetimeForm] = useState({ package: 'Premium', paymentMethod: 'cash' });

  const pageItems = useMemo(() => {
    // Server handles member/package/serial/branch/status/dates/duration/autoRenewal.
    // Fee + payment-method columns are payment-enriched — filter within the loaded page.
    const rows = membershipList.filter((m) => (
      matchAmountFilter(m.registrationFee, filters.registrationFees, formatCurrency)
      && matchAmountFilter(m.packageFee, filters.packageFees, formatCurrency)
      && matchAmountFilter(m.totalPaid, filters.totalPaid, formatCurrency)
      && matchAmountFilter(m.pendingAmount, filters.pendingAmount, formatCurrency)
      && matchSaleMethodFilters(m, filters)
      && matchTextFilter(membershipPaymentNoteDisplay(m), filters.paymentNote)
    ));
    if (rangeStart || rangeEnd) return sortMembershipsByDateAsc(rows);
    return rows;
  }, [membershipList, filters, rangeStart, rangeEnd]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const nextPage = () => setPage((p) => Math.min(totalPages, p + 1));
  const prevPage = () => setPage((p) => Math.max(1, p - 1));

  const selectedMembership = useMemo(
    () => membershipList.find((m) => m.id === selectedMembershipId),
    [membershipList, selectedMembershipId],
  );

  const handleExport = async (exporterFn) => {
    try {
      const result = await fetchMembershipsPage({
        search: debouncedSearch,
        filters: { ...debouncedFilters, rangeStart, rangeEnd },
        forExport: true,
      });
      const all = result.rows || [];
      const packages = await fetchAllPackages().catch(() => []);
      const durationByPackage = new Map(
        (packages || []).map((p) => [
          String(p.name || '').trim().toLowerCase(),
          String(p.duration || '').trim(),
        ]).filter(([, duration]) => duration),
      );
      // Same fee-enriched rows as the table (not the unfiltered full dump)
      let mapped = dedupeMemberships((all || []).map((row) => {
        const mappedRow = mapMembershipRow(row);
        if (!mappedRow.totalAmount) {
          mappedRow.totalAmount = membershipTotalAmount(mappedRow);
        }
        if (mappedRow.paymentDbId) {
          mappedRow.pendingAmount = membershipPendingAmount(mappedRow);
        }
        mappedRow.duration = membershipDurationDisplay(mappedRow, durationByPackage);
        return mappedRow;
      }));
      if (rangeStart || rangeEnd) {
        mapped = sortMembershipsByDateAsc(mapped);
      }
      exporterFn(mapped);
    } catch (err) {
      setError(err.message || 'Export failed');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    try {
      setDeleting(true);
      await deleteMembership(deleteTarget.id);
      setDeleteTarget(null);
      toast.showDeleted('Membership');
      await loadData();
    } catch (err) {
      const message = err.message || 'Failed to delete membership';
      setError(message);
      toast.showError(message);
    } finally {
      setDeleting(false);
    }
  };

  const openModal = (type, membershipId = '') => {
    setFormKey((k) => k + 1);
    setSelectedMembershipId(membershipId);
    setActiveModal(type);
    if (type === actionTypes.edit && membershipId) {
      const m = membershipList.find((item) => item.id === membershipId);
      if (m) {
        setEditForm({
          member: m.memberName || m.member || '',
          gymId: formatSerialDisplay(m.serialNo != null ? m.serialNo : m.gymId) || String(m.gymId || '—'),
          package: m.package || '',
          packageFee: Number.isFinite(Number(m.packageFee)) ? String(Number(m.packageFee)) : '',
          duration: durationMonthsFromStored(m.duration),
          entryDate: String(membershipDateDisplay(m) || '').replace(/^—$/, '').slice(0, 10),
          start: m.datesTbc ? '' : String(m.startDate || m.start || '').replace(/^—$/, ''),
          expiry: m.datesTbc ? '' : String(m.expiryDate || m.expiry || '').replace(/^—$/, ''),
          datesTbc: Boolean(m.datesTbc),
          paymentDbId: m.paymentDbId || null,
          paymentSplits: splitsFromMembership(m),
          registrationFee: Number(m.registrationFee) || 0,
          discountAmount: Number(m.discountAmount) || 0,
          personalTrainingFee: Number(m.personalTrainingFee) || 0,
          classesFee: Number(m.classesFee) || 0,
        });
      }
    }
    if (type === actionTypes.upgrade && membershipId) {
      const m = membershipList.find((item) => item.id === membershipId);
      if (m) setUpgradeForm((prev) => ({ ...prev, newPackage: m.package === 'Basic' ? 'Standard' : 'Premium' }));
    }
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedMembershipId('');
  };

  const paidPreview = useMemo(
    () => sumPaymentSplits(editForm.paymentSplits),
    [editForm.paymentSplits],
  );

  const totalDuePreview = useMemo(
    () => membershipTotalAmount(editForm),
    [editForm],
  );

  const balancePreview = useMemo(
    () => Math.max(0, Math.round((totalDuePreview - paidPreview) * 100) / 100),
    [totalDuePreview, paidPreview],
  );

  const handlePackageChange = (packageName) => {
    const pkg = packageRows.find(
      (p) => String(p.name || '').trim().toLowerCase() === String(packageName || '').trim().toLowerCase(),
    );
    const catalogPrice = Number(pkg?.price);
    const catalogReg = Number(pkg?.registrationFee);
    const catalogDuration = durationMonthsFromStored(pkg?.duration);
    setEditForm((prev) => {
      const next = {
        ...prev,
        package: packageName,
        packageFee: Number.isFinite(catalogPrice) ? String(catalogPrice) : (pkg ? '0' : prev.packageFee),
        registrationFee: Number.isFinite(catalogReg) ? catalogReg : (pkg ? 0 : prev.registrationFee),
      };
      if (catalogDuration) {
        next.duration = catalogDuration;
        if (prev.start) {
          next.expiry = addMonthsToEndDate(prev.start, catalogDuration);
          next.datesTbc = false;
        }
      }
      return next;
    });
  };

  const handleDurationChange = (monthsRaw) => {
    const months = Number(monthsRaw) || 0;
    setEditForm((prev) => {
      const next = { ...prev, duration: months || '' };
      if (prev.start && months > 0) {
        next.expiry = addMonthsToEndDate(prev.start, months);
        next.datesTbc = false;
      }
      return next;
    });
  };

  const handleStartChange = (start) => {
    setEditForm((prev) => {
      const next = { ...prev, start };
      const months = Number(prev.duration) || 0;
      if (start && months > 0) {
        next.expiry = addMonthsToEndDate(start, months);
        next.datesTbc = false;
      }
      return next;
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedMembership) return;
    if (!String(editForm.package || '').trim()) {
      toast.showError('Select or enter a package.');
      return;
    }
    const paid = sumPaymentSplits(editForm.paymentSplits);
    if (paid < 0) {
      toast.showError('Enter a valid paid amount.');
      return;
    }

    try {
      setSaving(true);
      const hasBothDates = Boolean(editForm.start && editForm.expiry);
      const datesTbc = editForm.datesTbc && !hasBothDates ? true : false;
      const packageFee = Math.max(0, Number(editForm.packageFee) || 0);
      const durationLabel = subscriptionDurationLabel(editForm.duration) || '';

      await updateMembershipSale(selectedMembership.id, {
        packageName: editForm.package.trim(),
        start: editForm.start || null,
        expiry: editForm.expiry || null,
        datesTbc: hasBothDates ? false : datesTbc,
        packageFee,
        paymentSplits: editForm.paymentSplits,
        paymentDbId: editForm.paymentDbId || selectedMembership.paymentDbId || null,
        registrationFee: editForm.registrationFee,
        discountAmount: editForm.discountAmount,
        personalTrainingFee: editForm.personalTrainingFee,
        classesFee: editForm.classesFee,
        entryDate: editForm.entryDate || '',
        duration: durationLabel,
      });

      closeModal();
      toast.showUpdated('Membership');
      await loadData();
    } catch (err) {
      const message = err.message || 'Failed to update membership';
      setError(message);
      toast.showError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpgrade = (e) => {
    e.preventDefault();
    if (!selectedMembership) return;
    setMembershipList((prev) =>
      prev.map((m) =>
        m.id === selectedMembership.id ? { ...m, package: upgradeForm.newPackage, status: 'active' } : m,
      ),
    );
    closeModal();
  };

  const handleShowReceipt = (membership) => {
    const row = membershipRowForReceipt(membership);
    if (!row) return;
    setReceipt(buildReceiptFromPaymentRow(row, { title: 'Membership', label: 'Subscription' }));
  };

  const handleFreeze = (e) => {
    e.preventDefault();
    if (!selectedMembership) return;
    setMembershipList((prev) =>
      prev.map((m) =>
        m.id === selectedMembership.id ? { ...m, frozen: true, status: 'frozen' } : m,
      ),
    );
    closeModal();
  };

  const handleLifetime = (e) => {
    e.preventDefault();
    if (!selectedMembership) return;
    setMembershipList((prev) =>
      prev.map((m) =>
        m.id === selectedMembership.id
          ? { ...m, lifetime: true, status: 'active', frozen: false, package: lifetimeForm.package, expiryDate: '—' }
          : m,
      ),
    );
    closeModal();
  };

  const renderMembershipSelect = () => (
    <div className="owner-form-group membership-form-full">
      <label className="owner-form-label" htmlFor="membership-select">Select Member</label>
      <select
        id="membership-select"
        className="owner-form-select"
        value={selectedMembershipId}
        onChange={(e) => setSelectedMembershipId(e.target.value)}
        required
      >
        <option value="">Choose a member...</option>
        {membershipList.map((m) => (
          <option key={m.id} value={m.id}>
            {m.memberName} — {m.package} ({m.status})
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div>
      <PageHeader
        title="Memberships"
        subtitle="New package subscriptions from the Subscription page"
      />

      <PrintActionsHeader
        startDate={rangeStart}
        endDate={rangeEnd}
        onStartDateChange={setRangeStart}
        onEndDateChange={setRangeEnd}
        onClearDates={() => { setRangeStart(''); setRangeEnd(''); }}
        onPrint={() => handleExport(printMembershipList)}
        onCsv={() => handleExport(exportMembershipCsv)}
        onPdf={() => handleExport(exportMembershipPdf)}
        onExcel={() => handleExport(exportMembershipExcel)}
        onExport={() => handleExport(exportMemberships)}
        ariaLabel="Membership print and export"
      >
        <input
          type="search"
          className="owner-form-input"
          style={{ maxWidth: 260 }}
          placeholder="Search name, serial, or phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search memberships"
        />
      </PrintActionsHeader>

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading memberships...</p>}

      <div className="owner-card rec-panel-card rec-member-list-card admin-list-table-card">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table admin-list-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Entry Date"
                  filterKey="date"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Serial No"
                  filterKey="serialNo"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search serial..."
                />
                <ColumnFilterHeader
                  label="Member"
                  filterKey="member"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search member..."
                />
                <ColumnFilterHeader
                  label="Branch"
                  filterKey="branch"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search branch..."
                />
                <ColumnFilterHeader
                  label="Package"
                  filterKey="package"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search package..."
                />
                <ColumnFilterHeader
                  label="Duration"
                  filterKey="duration"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search duration..."
                />
                <ColumnFilterHeader
                  label="Registration Fees"
                  filterKey="registrationFees"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search amount..."
                />
                <ColumnFilterHeader
                  label="Package Fees"
                  filterKey="packageFees"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search amount..."
                />
                <ColumnFilterHeader
                  label="Amount Paid"
                  filterKey="totalPaid"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search amount..."
                />
                <SaleMethodColumnHeaders
                  sales={membershipList}
                  filters={filters}
                  openFilterKey={openFilterKey}
                  toggleFilter={toggleFilter}
                  updateFilter={updateFilter}
                />
                <ColumnFilterHeader
                  label="Pending Amount"
                  filterKey="pendingAmount"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search amount..."
                />
                <ColumnFilterHeader
                  label="Start Date"
                  filterKey="startDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Expiry Date"
                  filterKey="expiryDate"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Auto Renewal"
                  filterKey="autoRenewal"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search yes/no..."
                />
                <ColumnFilterHeader
                  label="Payment Note"
                  filterKey="paymentNote"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search note..."
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search status..."
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.length > 0 ? (
                pageItems.map((m) => (
                  <tr key={m.id}>
                    <td>{membershipDateDisplay(m)}</td>
                    <td className="rec-member-gym-id">
                      {m.serialNo != null
                        ? formatSerialDisplay(m.serialNo)
                        : (m.gymId && m.gymId !== '—' ? m.gymId : '—')}
                    </td>
                    <td>{m.memberName}</td>
                    <td>{m.branch || '—'}</td>
                    <td>{m.package}</td>
                    <td>{m.duration || '—'}</td>
                    <td>{formatCurrency(m.registrationFee)}</td>
                    <td>{formatCurrency(m.packageFee)}</td>
                    <td className="finance-amount-positive">{formatCurrency(m.totalPaid)}</td>
                    <SaleMethodCells row={m} />
                    <td className={m.pendingAmount > 0 ? 'finance-amount-negative' : ''}>
                      {formatCurrency(m.pendingAmount)}
                    </td>
                    <td>{membershipStartDisplay(m)}</td>
                    <td>{membershipExpiryDisplay(m)}</td>
                    <td>{m.autoRenewal ? 'Yes' : 'No'}</td>
                    <td>{membershipPaymentNoteDisplay(m)}</td>
                    <td><StatusBadge membership={m} /></td>
                    <td>
                      <TableRowIconActions
                        onEdit={() => openModal(actionTypes.edit, m.id)}
                        onDelete={() => setDeleteTarget(m)}
                        editLabel={`Edit ${m.memberName || m.member}`}
                        deleteLabel={`Delete ${m.memberName || m.member}`}
                      >
                        {membershipCanPayBalance(m) && (
                          <button
                            type="button"
                            className="owner-btn owner-btn-primary owner-btn-sm"
                            onClick={() => setPayTarget(m)}
                          >
                            Pay pending
                          </button>
                        )}
                        <button
                          type="button"
                          className="owner-btn owner-btn-secondary owner-btn-sm"
                          onClick={() => handleShowReceipt(m)}
                        >
                          Receipt
                        </button>
                        <button
                          type="button"
                          className="owner-btn owner-btn-secondary owner-btn-sm"
                          onClick={() => openModal(actionTypes.upgrade, m.id)}
                        >
                          Upgrade
                        </button>
                        {!m.frozen && !m.lifetime && (
                          <button
                            type="button"
                            className="owner-btn owner-btn-secondary owner-btn-sm"
                            onClick={() => openModal(actionTypes.freeze, m.id)}
                          >
                            Freeze
                          </button>
                        )}
                      </TableRowIconActions>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={16 + SALE_METHOD_COLUMN_COUNT} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '24px' }}>
                    No memberships found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          from={from}
          to={to}
          onPageChange={setPage}
          onPrev={prevPage}
          onNext={nextPage}
        />
      </div>

      {activeModal === actionTypes.edit && (
        <div className="subscription-modal-overlay" onClick={closeModal}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Edit Membership</h2>
              <button type="button" className="subscription-modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <form key={formKey} className="subscription-form" onSubmit={handleEdit}>
              <div className="membership-form-grid">
                <div className="owner-form-group membership-form-full">
                  <label className="owner-form-label" htmlFor="admin-edit-gym-id">Serial No</label>
                  <input
                    id="admin-edit-gym-id"
                    type="text"
                    className="owner-form-input rec-member-country-readonly"
                    value={editForm.gymId}
                    readOnly
                  />
                </div>
                <div className="owner-form-group membership-form-full">
                  <label className="owner-form-label" htmlFor="admin-edit-member">Member</label>
                  <input
                    id="admin-edit-member"
                    type="text"
                    className="owner-form-input"
                    value={editForm.member}
                    readOnly
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="admin-edit-package">Package</label>
                  <input
                    id="admin-edit-package"
                    type="text"
                    className="owner-form-input"
                    list="admin-edit-membership-package-list"
                    value={editForm.package}
                    onChange={(e) => handlePackageChange(e.target.value)}
                    placeholder="Select or type package name"
                    required
                    autoComplete="off"
                  />
                  <datalist id="admin-edit-membership-package-list">
                    {packageRows.map((pkg) => (
                      <option
                        key={pkg.id || pkg.name}
                        value={pkg.name}
                        label={Number(pkg.price) > 0 ? `${pkg.name} — ${formatCurrency(pkg.price)}` : pkg.name}
                      />
                    ))}
                  </datalist>
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="admin-edit-package-fee">Package Fee</label>
                  <input
                    id="admin-edit-package-fee"
                    type="text"
                    inputMode="decimal"
                    className="owner-form-input"
                    value={editForm.packageFee == null ? '' : String(editForm.packageFee)}
                    onChange={(e) => setEditForm((p) => ({ ...p, packageFee: e.target.value }))}
                    placeholder="Optional — leave blank (shows as 0)"
                    disabled={selectedMembership?.complimentary}
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="admin-edit-registration-fee">Registration Fee</label>
                  <input
                    id="admin-edit-registration-fee"
                    type="text"
                    inputMode="decimal"
                    className="owner-form-input"
                    value={editForm.registrationFee == null ? '' : String(editForm.registrationFee)}
                    onChange={(e) => setEditForm((p) => ({ ...p, registrationFee: e.target.value }))}
                    placeholder="0"
                    disabled={selectedMembership?.complimentary}
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="admin-edit-duration">Duration</label>
                  <select
                    id="admin-edit-duration"
                    className="owner-form-select"
                    value={editForm.duration === '' || editForm.duration == null ? '' : String(editForm.duration)}
                    onChange={(e) => handleDurationChange(e.target.value)}
                  >
                    <option value="">Select duration</option>
                    {SUBSCRIPTION_DURATION_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="admin-edit-entry-date">Entry / Sale Date</label>
                  <input
                    id="admin-edit-entry-date"
                    type="date"
                    className="owner-form-input"
                    value={editForm.entryDate || ''}
                    onChange={(e) => setEditForm((p) => ({ ...p, entryDate: e.target.value }))}
                  />
                  <p className="owner-placeholder-text" style={{ marginTop: 4, marginBottom: 0 }}>
                    Cash, Z Report, petty cash, and dashboard revenue use this date.
                  </p>
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="admin-edit-start">Membership Start</label>
                  <input
                    id="admin-edit-start"
                    type="date"
                    className="owner-form-input"
                    value={editForm.start === '—' ? '' : (editForm.start || '')}
                    onChange={(e) => handleStartChange(e.target.value)}
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="admin-edit-expiry">Membership End</label>
                  <input
                    id="admin-edit-expiry"
                    type="date"
                    className="owner-form-input"
                    value={editForm.expiry === '—' ? '' : (editForm.expiry || '')}
                    onChange={(e) => setEditForm((p) => ({ ...p, expiry: e.target.value }))}
                  />
                </div>
                <div className="owner-form-group membership-form-full">
                  <SplitPaymentFields
                    idPrefix="admin-edit-membership"
                    splits={editForm.paymentSplits}
                    onChange={(paymentSplits) => setEditForm((p) => ({ ...p, paymentSplits }))}
                    disabled={saving}
                  />
                  <p className="owner-placeholder-text rec-sale-payment-hint" style={{ marginTop: 8 }}>
                    Total due: <strong>{formatCurrency(totalDuePreview)}</strong>
                    {' · '}
                    Amount paid: <strong>{formatCurrency(paidPreview)}</strong>
                    {' · '}
                    Balance: <strong>{formatCurrency(balancePreview)}</strong>
                  </p>
                </div>
              </div>
              {editForm.datesTbc && (
                <p className="owner-placeholder-text" style={{ marginTop: 0 }}>
                  Entry / Sale Date controls financial reports. Set membership start and end when the member begins gym — saving both dates clears TBC.
                </p>
              )}
              <div className="subscription-form-actions">
                <button type="submit" className="owner-btn owner-btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
                <button type="button" className="owner-btn owner-btn-secondary" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeModal === actionTypes.upgrade && (
        <div className="subscription-modal-overlay" onClick={closeModal}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Upgrade Membership</h2>
              <button type="button" className="subscription-modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <form key={formKey} className="subscription-form" onSubmit={handleUpgrade}>
              {renderMembershipSelect()}
              <div className="membership-form-grid">
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="upgrade-package">New Package</label>
                  <select
                    id="upgrade-package"
                    className="owner-form-select"
                    value={upgradeForm.newPackage}
                    onChange={(e) => setUpgradeForm((p) => ({ ...p, newPackage: e.target.value }))}
                  >
                    {packageOptions.map((pkg) => (
                      <option key={pkg} value={pkg}>{pkg}</option>
                    ))}
                  </select>
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="upgrade-payment">Payment Method</label>
                  <select
                    id="upgrade-payment"
                    className="owner-form-select"
                    value={upgradeForm.paymentMethod}
                    onChange={(e) => setUpgradeForm((p) => ({ ...p, paymentMethod: e.target.value }))}
                  >
                    <option value="cash">Cash</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="jazzcash">JazzCash</option>
                    <option value="easypaisa">EasyPaisa</option>
                  </select>
                </div>
              </div>
              <div className="subscription-form-actions">
                <button type="submit" className="owner-btn owner-btn-primary" disabled={!selectedMembershipId}>
                  Upgrade Membership
                </button>
                <button type="button" className="owner-btn owner-btn-secondary" onClick={closeModal}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeModal === actionTypes.freeze && (
        <div className="subscription-modal-overlay" onClick={closeModal}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Freeze Membership</h2>
              <button type="button" className="subscription-modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <form key={formKey} className="subscription-form" onSubmit={handleFreeze}>
              {renderMembershipSelect()}
              <div className="membership-form-grid">
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="freeze-days">Freeze Duration (days)</label>
                  <input
                    id="freeze-days"
                    type="number"
                    min="1"
                    className="owner-form-input"
                    value={freezeForm.freezeDays}
                    onChange={(e) => setFreezeForm((p) => ({ ...p, freezeDays: e.target.value }))}
                    required
                  />
                </div>
                <div className="owner-form-group membership-form-full">
                  <label className="owner-form-label" htmlFor="freeze-reason">Reason</label>
                  <textarea
                    id="freeze-reason"
                    className="owner-form-input owner-form-textarea"
                    rows={3}
                    value={freezeForm.reason}
                    onChange={(e) => setFreezeForm((p) => ({ ...p, reason: e.target.value }))}
                    placeholder="Optional reason for freezing"
                  />
                </div>
              </div>
              <div className="subscription-form-actions">
                <button type="submit" className="owner-btn owner-btn-primary" disabled={!selectedMembershipId}>
                  Freeze Membership
                </button>
                <button type="button" className="owner-btn owner-btn-secondary" onClick={closeModal}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {activeModal === actionTypes.lifetime && (
        <div className="subscription-modal-overlay" onClick={closeModal}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Lifetime Membership</h2>
              <button type="button" className="subscription-modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <form key={formKey} className="subscription-form" onSubmit={handleLifetime}>
              {renderMembershipSelect()}
              <p className="membership-modal-message">
                Convert the selected member to a lifetime membership with no expiry date.
              </p>
              <div className="membership-form-grid">
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="lifetime-package">Package</label>
                  <select
                    id="lifetime-package"
                    className="owner-form-select"
                    value={lifetimeForm.package}
                    onChange={(e) => setLifetimeForm((p) => ({ ...p, package: e.target.value }))}
                  >
                    {packageOptions.map((pkg) => (
                      <option key={pkg} value={pkg}>{pkg}</option>
                    ))}
                  </select>
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="lifetime-payment">Payment Method</label>
                  <select
                    id="lifetime-payment"
                    className="owner-form-select"
                    value={lifetimeForm.paymentMethod}
                    onChange={(e) => setLifetimeForm((p) => ({ ...p, paymentMethod: e.target.value }))}
                  >
                    <option value="cash">Cash</option>
                    <option value="bank">Bank Transfer</option>
                    <option value="jazzcash">JazzCash</option>
                    <option value="easypaisa">EasyPaisa</option>
                  </select>
                </div>
              </div>
              <div className="subscription-form-actions">
                <button type="submit" className="owner-btn owner-btn-primary" disabled={!selectedMembershipId}>
                  Convert to Lifetime
                </button>
                <button type="button" className="owner-btn owner-btn-secondary" onClick={closeModal}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {payTarget?.paymentDbId && (
        <PayBalanceModal
          key={payTarget.paymentDbId}
          paymentRow={membershipToPaymentRow(payTarget)}
          saleMeta={{ title: 'Membership', label: 'Subscription' }}
          onClose={() => setPayTarget(null)}
          onPaid={async (receiptData) => {
            setReceipt(receiptData);
            await loadData();
          }}
        />
      )}

      <SaleReceiptModal receipt={receipt} onClose={() => setReceipt(null)} />

      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="subscription-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Membership</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="member-modal-message">
              Delete membership for <strong>{deleteTarget.memberName || deleteTarget.member}</strong>
              {deleteTarget.package ? <> ({deleteTarget.package})</> : null}? This cannot be undone.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn member-btn-danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete Membership'}
              </button>
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MembershipManagement;
